<template>
  <div class="article-page">
    <div class="article-container">
      <div class="main-layout">
        <!-- 左侧悬浮按钮组：目录 | 返回，增加动态class控制显隐 -->
        <div class="tool-group glass" :class="{ 'tool-group-hidden': isScrolling }">
          <button class="inner-btn" @click="drawerOpen = !drawerOpen">
            目录
          </button>
          <span class="divider-line">|</span>
          <button class="inner-btn" @click="router.back()">
            返回
          </button>
        </div>
        <!-- 抽屉遮罩 -->
        <div v-if="drawerOpen" class="drawer-mask" @click="drawerOpen = false"></div>

        <!-- 抽屉容器：内部保留原来 sidebar-toc + 原有sticky逻辑，完全复用旧toc样式 -->
        <aside class="sidebar-drawer" :class="{ open: drawerOpen }">
          <!-- 【重点】原来的sidebar-toc原样保留，sticky不变 -->
          <div class="sidebar-toc">
            <div class="toc-card glass">
              <div class="toc-inner">
                <div class="toc-header-row">
                  <h4 class="toc-header">目录</h4>
                  <button class="drawer-close" @click="drawerOpen = false">✕</button>
                </div>
                <ul class="toc-list">
                  <li v-for="item in tocList" :key="item.anchor" :class="['toc-item', `level-${item.level}`]">
                    <a class="toc-link" @click="scrollTo(item.anchor); drawerOpen = false;">{{ item.title }}</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </aside>

        <!-- 右侧：全部内容放在同一个白色大卡片里面（完全原样，无改动） -->
        <main class="article-content">
          <div class="content-wrap-card">
            <div class="article-box">
              <h1 class="article-title">{{ article.title }}</h1>
              <!-- 修改标签部分：flex横向标签卡片 -->
              <div class="article-meta">
                <span v-if = 'article.createTime'>创建时间:{{ article.createTime }}</span>
                <span v-if = 'article.updateTime'>最近更新时间:{{ article.updateTime }}</span>
                <span>阅读:{{ article.viewCount }}次</span>
                <div class="meta-tag-wrap">
                  <span>标签:</span>
                  <div class="meta-tag" v-for="tech in article.categoryList" :key="tech" :style="JSON.parse(tech.style)">
                    {{ tech.categoryName }}
                  </div>
                </div>
              </div>
              <hr class="divider"/>
              <div class="markdown" v-html="renderHtml"></div>
            </div>

            <!-- 上一篇、下一篇：左右分开 -->
            <div class="prev-next-block">
              <div v-if="prevArticleComputed" class="prev-item">
                <div class="label">上一篇</div>
                <router-link :to="{
                  name: 'blogDetail',
                  query: {
                    id: prevArticleComputed.id
                  }
                }">
                  {{ prevArticle.title }}
                </router-link>
              </div>
              <div v-else class="prev-item">
                <div class="label">上一篇</div>
                已经到顶了～
              </div>
              <div v-if="nextArticleComputed" class="next-item">
                <div class="label">下一篇</div>
                <router-link :to="{
                  name: 'blogDetail',
                  query: {
                    id: nextArticleComputed.id
                  }
                }">
                  {{ nextArticle.title }}
                </router-link>
              </div>
              <div v-else class="next-item">
                <div class="label">下一篇</div>
                已经到头了～
              </div>
            </div>

            <!-- 相关推荐，复用 LaputaBlogCard 组件 -->
            <div class="recommend-block">
              <h3 class="rec-title">相关推荐</h3>
              <div class="rec-list">
                <LaputaBlogCard
                    v-for="rec in recommendList"
                    :key="rec.id"
                    :blogCard="rec"
                    @click="router.push({
                    name:'blogDetail',
                    query:{
                      id: rec.id
                    }
                   })"
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, onMounted, nextTick, watch, onUnmounted} from 'vue'
import {useRouter, useRoute} from 'vue-router'
import {useStore} from "vuex";
import scrollTo from "@/utils/scrollTo";
import LaputaBlogCard from '@/components/blog/LaputaBlogCard.vue'
import request from "@/api/request";
import showToast from "@/utils/toast";
import marked from "@/hooks/markdown";
// 【新增抽屉开关，仅此一行】
const drawerOpen = ref(false)
// ========== 新增滚动控制变量 ==========
const isScrolling = ref(false)
let scrollTimer = null
const store = useStore()
function handleScroll() {
  isScrolling.value = true
  // 清除旧定时器
  clearTimeout(scrollTimer)
  // 滚动停止 150ms 后恢复显示按钮
  scrollTimer = setTimeout(() => {
    isScrolling.value = false
  }, 150)
}
const router = useRouter()
const route = useRoute()

const article = ref({
  id: 0,
  createTime: null,
  updateTime: null,
  content: '',
  title: '',
  viewCount: 0,
  categoryList:[]
})
const prevArticle = ref(null)
const prevArticleComputed = computed(() => prevArticle.value)
const nextArticle = ref(null)
const nextArticleComputed = computed(() => nextArticle.value)
//相关查询
const recommendList = ref([])
const tocList = ref([])

marked.setOptions({
  gfm: true,
  breaks: true
})
//正文
const renderHtml = computed(() => {
  if (!article.value.content) return ''
  return marked.parse(article.value.content)
})
//全局搜索按钮
watch(() => store.state.searchbar.blogDetailSearchKeyword,async (newVal)=>{
  if (store.state.searchbar.search && newVal){
    const tocId = getFirstMatchedValue(newVal)
    scrollTo(tocId)
  }
})
// 初始化 + 切换query.id时触发
watch(
    () => route.query.id,
    (newId) => {
      if (newId) {
        getArticle(newId)
      }
    },
    {immediate: true} // immediate=true：页面刚进来也执行一次，等价onMounted加载
)
const tocMap = new Map()

function getFirstMatchedValue(keyword) {
  for (const [key, val] of tocMap) {
    if (key.toLowerCase().includes(keyword.toLowerCase())) {
      return val
    }
  }
  return ""
}

// 生成目录，toc_数字锚点，无中文横杠
function generateToc(mdText) {
  tocList.value = []
  const lines = mdText.split('\n')
  let inCodeBlock = false
  let idx = 0
  const headingReg = /^(#{1,3})\s+(.*)$/

  for (const line of lines) {
    // 检测代码块开关 ```
    if (/^```/.test(line.trimStart())) {
      inCodeBlock = !inCodeBlock
      continue
    }
    // 如果当前在代码块内部，直接跳过
    if (inCodeBlock) continue

    const match = headingReg.exec(line)
    if (match) {
      const level = match[1].length
      const title = match[2].trim()
      const anchor = 'toc_' + idx
      tocMap.set(title,anchor)
      tocList.value.push({ level, title, anchor })
      idx++
    }
  }
}


function injectHeadingId() {
  const markdownDom = document.querySelector('.markdown')
  if (!markdownDom) return
  const headers = markdownDom.querySelectorAll('h1,h2, h3')
  headers.forEach((h, index) => {
    h.id = 'toc_' + index
  })
}

const queryRecommendList = async () => {
  try {
    const response = await request.post(`/v1/api/queryRecommendList`, {
      categoryList: route.query.categoryList
    })
    const resp = response.data
    if (resp.responseCode === '0000') {
      recommendList.value = resp.data;
    } else {
      showToast(resp.responseDesc)
    }
  } catch (e) {
    showToast(e.message)
  }
}

// mock数据，替换为后端接口
async function getArticle(id) {
  try {
    const response = await request.get(`/v1/api/queryBlogContentById?id=${id}`)
    const resp = response.data
    if (resp.responseCode === '0000') {
      article.value = resp.data.curr;
      generateToc(article.value.content)
      //等v‑html渲染DOM完成，再给h2/h3设置id
      await nextTick()
      injectHeadingId()
      setTimeout(() => {

      }, 0)
      prevArticle.value = resp.data.prev
      nextArticle.value = resp.data.next
    } else {
      article.value.title = '文章不存在'
      article.value.content = '# 404\n没有找到该文章'
      showToast(resp.responseCode)
    }
  } catch (e) {
    article.value.title = '文章不存在'
    article.value.content = '# 404\n没有找到该文章'
    showToast(e.message)
  }
}

onMounted(() => {
  //getArticle(aid)
  queryRecommendList()
  // 挂载监听滚动
  window.addEventListener('scroll', handleScroll)
})

// 组件销毁，移除监听，防止内存泄漏
onUnmounted(()=>{
  window.removeEventListener('scroll', handleScroll)
  clearTimeout(scrollTimer)
})
</script>

<style scoped>
/* 页面整体浅绿色背景，width:100%铺满屏幕 */
.article-page {
  width: 100%;
  min-height: 100vh;
  padding: 100px 20px 40px;
  background-color: var(--laputa-body-background-other);
  box-sizing: border-box;
}

.article-container {
  width: 100%;
}

.main-layout {
  display: flex;
  gap: 20px;
}

/* 左侧悬浮按钮组容器，增加transition过渡动画 */
.tool-group {
  position: fixed;
  right: 40px;
  bottom: 120px;
  z-index: 90;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  /* 动画：透明度+位移 */
  transition: all 0.22s ease;
  opacity: 1;
  transform: translateY(0);
  border-color: var(--laputa-green);
}
/* 滚动时隐藏样式 */
.tool-group-hidden{
  opacity: 0;
  transform: translateY(-12px);
  pointer-events: none; /* 隐藏的时候禁止点击 */
}

.tool-group:hover{
  border-color: var(--laputa-green);
}
.inner-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  font-size: var(--laputa-fontsize-15);
}
.inner-btn:hover{
  color: var(--laputa-green);
}
.divider-line {
  color: var(--laputa-glass-header-separator);
}

/* ========== 抽屉遮罩 新增 ========== */
.drawer-mask {
  position: fixed;
  inset: 0;
  background: var(--laputa-card-background-deep-mask-gray);
  z-index: 98;
}

/* ========== 抽屉容器 新增 ========== */
.sidebar-drawer {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 99;
  transform: translateX(-100%);
  transition: transform 0.24s ease;
  padding: 100px 16px 20px;
  box-sizing: border-box;
  /* 抽屉内部允许滚动，但是sidebar-toc继续sticky */
  overflow-y: auto;
}

.sidebar-drawer.open {
  transform: translateX(0);
}

/* 【重点】原来的sidebar-toc原样保留，sticky属性不动！ */
.sidebar-toc {
  width: 240px;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  align-self: flex-start;
  max-height: calc(100vh - 100px);
}

.toc-card {
  border-radius: 18px;
  padding: 20px;
  overflow-y: auto;
  max-height: calc(100vh - 100px);
}

.toc-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.toc-header {
  font-size: var(--laputa-fontsize-15);
  margin: 0;
  font-weight:400;
}

.drawer-close {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: var(--laputa-fontsize-16);
  color: var(--laputa-close);
}
.drawer-close:hover{
  color: var(--laputa-close-hover);
}
.toc-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: var(--laputa-fontsize-14);
}

.toc-item {
  margin: 9px 0;
}

.toc-item.level-1 {
  padding-left: 15px;
  font-weight: bold;
}

.toc-item.level-2 {
  padding-left: 20px;
  font-weight: 600;
}

.toc-item.level-3 {
  padding-left: 25px;
}
.toc-item.level-4 {
  padding-left: 30px;
}

.toc-link {
  color: var(--laputa-common-font-color);
  text-decoration: none;
  cursor: pointer;
  font-weight: 400;
}

.toc-link:hover {
  color: var(--laputa-green);
}
/* 右侧：统一白色大卡片，所有内容都在里面 */
.article-content {
  flex: 1;
  /* 关键：阻止子元素溢出把flex容器撑大 */
  min-width: 0;
  border-radius: 20px;
}

.content-wrap-card {
  background-color: var(--laputa-content);
  padding: 20px;
  /* 核心：内部横向溢出出现滚动条，而不是撑大整个卡片 */
  overflow-x: auto;
  border-radius: 24px;

}

.article-box {
  margin-bottom: 36px;
}

.article-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 12px;
  text-align: center;
}

.article-meta {
  font-size: 14px;
  color: var(--laputa-common-sub-font-color);
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  justify-content: center;
}

/* 标签胶囊横向卡片 */
.meta-tag-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.meta-tag {
  padding: 3px 10px;
  background-color: #edf2f7;
  border-radius: 12px;
  font-size: 13px;
}

.divider {
  border: 0;
  border-bottom: 1px solid #d8ddd9;
  margin: 22px 0;
}

/* 上一篇、下一篇：左右分开 */
.prev-next-block {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.prev-item {
  flex: 1;
  border: 1px solid var(--laputa-green);
  border-radius: 14px;
  padding: 18px;
}

.next-item {
  flex: 1;
  text-align: right;
  border: 1px solid var(--laputa-green);
  border-radius: 14px;
  padding: 18px;
}

.label {
  font-size: 13px;
  color: var(--laputa-common-secondary-font-color);
  margin-bottom: 6px;
}

/* 相关推荐：一行3个 */
.recommend-block {
}

.rec-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 18px;
}

.rec-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

/* 移动端适配：原来隐藏sidebar-toc的代码删掉，抽屉在移动端直接可用 */
@media (max-width: 1024px) {
  .article-page {
    padding-top: 70px;
  }

  /* 删掉原来 display:none; 移动端直接使用抽屉 */
  .prev-next-block {
    flex-direction: column;
  }

  .next-item {
    text-align: left;
  }

  .rec-list {
    grid-template-columns:1fr;
  }

  .content-wrap-card {
    padding: 20px;
  }
}
</style>

